import { RegularBreakpoints } from '@mui/material';

import { phoneNumberInput } from '@/constants/form_inputs';
import { PropertyStatus } from '@/models';

export const CustodyActionSectionFields = [
  {
    name: 'actionDateTime',
    type: 'datetime',
    label: 'Current date & time',
    allowFreeInput: true,
    rules: {
      required: 'This field is required.',
    },
    grid: {
      md: 6,
    },
  },
  {
    name: 'officer',
    type: 'autocomplete',
    label: 'Officer',
    rules: {
      required: 'This field is required.',
    },
    grid: {
      md: 6,
    },
  },
];

export const CheckInFormFields = [
  {
    name: 'inventoryLocation',
    type: 'autocomplete',
    label: 'Inventory location',
  },
  {
    name: 'notes',
    label: 'Notes',
    type: 'text',
    rules: {
      required: 'This field is required.',
      message: 'Notes should be less than 300 characters.',
    },
    multiline: true,
    rows: 6,
  },
];

export const CustodyCheckoutCourtFormFields = [
  {
    name: 'vendor',
    type: 'autocomplete',
    label: 'Court name',
    rules: {
      required: 'This field is required.',
    },
  },
  {
    name: 'expectedBackDate',
    label: 'Date expected back',
    type: 'date',
  },
  {
    name: 'trackingNumber',
    label: 'Tracking number',
  },
  {
    name: 'signature',
    type: 'signature',
    label: 'Signature',
  },
  {
    name: 'notes',
    label: 'Notes',
    type: 'text',
    rules: {
      maxLength: {
        value: 300,
        message: 'Notes should be less than 300 characters.',
      },
    },
    multiline: true,
    rows: 10,
  },
];

export const ToPersonFormFields = [
  {
    name: 'signature',
    type: 'signature',
    label: 'Signature',
  },
  {
    name: 'notes',
    label: 'Notes',
    type: 'text',
    multiline: true,
    rows: 6,
  },
];

export const ReleaseFormFields = [
  {
    name: 'notes',
    label: 'Notes',
    type: 'text',
    rules: {
      message: 'Notes should be less than 300 characters.',
    },
    multiline: true,
    rows: 10,
  },
];

export const RelaseToOwnerFormFields = [
  {
    name: 'owner',
    label: 'Name',
    disabled: true,
  },
  ...ReleaseFormFields,
];

export const ToOtherAgencyFormFields = [
  {
    title: 'Agency Details',
    name: 'agency',
    items: [
      {
        name: 'name',
        label: 'Agency name',
        grid: {
          md: 6,
        },
      },
      {
        ...phoneNumberInput,
        name: 'phoneNumber',
        label: 'Agency phone number',
        grid: {
          md: 5,
        },
      },
    ],
  },
  {
    title: 'Agency contact person',
    name: 'agency.contact',
    items: [
      {
        name: 'name',
        label: 'Name',
        grid: {
          md: 11,
        },
      },
      {
        ...phoneNumberInput,
        name: 'phoneNumber',
        label: 'Phone number',
        grid: {
          md: 11,
        },
      },
    ],
  },
  {
    title: 'Released to ',
    name: 'user',
    items: [
      {
        name: 'name',
        label: 'Name',
        grid: {
          md: 5,
        },
      },
      {
        name: 'badgeNumber',
        label: 'Badge number',

        grid: {
          md: 5,
        },
      },
    ],
  },

  {
    title: 'Other',
    items: [
      {
        name: 'expectedBackDate',
        label: 'Date expected back',
        type: 'date',
        grid: {
          md: 11,
        },
      },

      {
        name: 'signature',
        type: 'signature',
        label: 'Signature',
        grid: {
          md: 11,
        },
      },
      {
        name: 'notes',
        label: 'Comment',
        type: 'text',
        multiline: true,
        rules: {
          message: 'Notes should be less than 300 characters.',
        },
        rows: 10,
        grid: {
          md: 11,
        },
      },
    ],
  },
];

export type IPropertyLogViewItem = {
  key: string;
  label: string;
  type?: string;
  style?: RegularBreakpoints;
};

export type IPropertyDetailField = {
  key: string;
  label: string;
  type?: 'date' | 'user-date' | 'masterName';
  grid?: { [key: string]: number };
};

export const PropertyDetailsGeneralFields: IPropertyDetailField[] = [
  { key: 'number', label: 'Property ID' },
  { key: 'name', label: 'Property name' },
  { key: 'typeTag', label: 'Property type tag' },
  { key: 'involvement', label: 'Involvement' },
  { key: 'description', label: 'Description', grid: { xs: 6 } },
  { key: 'owner', label: 'Item owner', type: 'masterName' },
  { key: 'value', label: 'Value' },
  { key: 'loggedInfo', label: 'Logged', type: 'user-date', grid: { xs: 6 } },
  {
    key: 'inboundTransferredInfo',
    label: 'Transferred',
    type: 'user-date',
    grid: { xs: 6 },
  },
  { key: 'caseId', label: 'Case ID' },
  { key: 'cfsId', label: 'CFS ID' },
  { key: 'type', label: 'Type' },
];

export const PropertyTypeSpecificFields: Record<
  string,
  IPropertyDetailField[]
> = {
  cash: [
    { key: 'cash.amount', label: 'Amount' },
    { key: 'cash.currency', label: 'Currency' },
  ],
  jewelry: [{ key: 'jewelry.type', label: 'Jewelry type' }],
  alcohol: [{ key: 'alcohol.brand', label: 'Brand' }],
  drugs: [
    { key: 'drugs.type', label: 'Drug type' },
    { key: 'drugs.quantity', label: 'Quantity' },
  ],
  firearm: [
    { key: 'firearm.type', label: 'Firearm type' },
    { key: 'firearm.make', label: 'Make' },
    { key: 'firearm.model', label: 'Model' },
  ],
  knife: [
    { key: 'knife.make', label: 'Make' },
    { key: 'knife.model', label: 'Model' },
  ],
  explosive: [{ key: 'explosive.type', label: 'Type' }],
  vehicle: [
    { key: 'vehicle.plateNumber', label: 'Plate number' },
    { key: 'vehicle.make', label: 'Make' },
    { key: 'vehicle.model', label: 'Model' },
    { key: 'vehicle.color', label: 'Color' },
  ],
};

export const PropertyLogsMap = {
  [PropertyStatus.LOGGED]: {
    statusLabel: 'LOGGED',
    items: [{ key: 'notes', label: 'Notes', style: { xs: 12 } }],
  },
  [PropertyStatus.AUCTIONED]: {
    statusLabel: 'Auctioned',
    items: [{ key: 'notes', label: 'Notes', style: { xs: 12 } }],
  },
  [PropertyStatus.DESTROYED]: {
    statusLabel: 'Destroyed',
    items: [{ key: 'notes', label: 'Notes', style: { xs: 12 } }],
  },
  [PropertyStatus.DISPOSED]: {
    statusLabel: 'Disposed',
    items: [{ key: 'notes', label: 'Notes', style: { xs: 12 } }],
  },
  [PropertyStatus.IN_POLICE_CUSTODY]: {
    statusLabel: 'In Police Custody',
    items: [
      { key: 'inventoryLocation.name', label: 'Inventory location' },
      {
        key: 'notes',
        label: 'Notes',
        style: { xs: 12 },
      },
    ],
  },
  [PropertyStatus.RELEASED_TO_COURT]: {
    statusLabel: 'Released to Court',
    items: [
      { key: 'vendor.name', label: 'Court name', style: { xs: 4 } },
      {
        key: 'expectedBackDate',
        label: 'Date expected back',
        type: 'date',
        style: { xs: 4 },
      },
      { key: 'trackingNumber', label: 'Tracking number', style: { xs: 4 } },
      {
        key: 'notes',
        label: 'Notes',
        style: { xs: 12 },
      },
      { key: 'signature', label: 'Signature', style: { xs: 12 } },
    ],
  },
  [PropertyStatus.RELEASED_TO_LAB]: {
    statusLabel: 'Release to Lab',
    items: [
      { key: 'vendor.name', label: 'Lab name' },
      { key: 'expectedBackDate', label: 'Date expected back', type: 'date' },
      { key: 'trackingNumber', label: 'Tracking number' },
      {
        key: 'notes',
        label: 'Notes',
        style: { xs: 12 },
      },
      { key: 'signature', label: 'Signature', style: { xs: 12 } },
    ],
  },
  [PropertyStatus.RELEASED_TO_OWNER]: {
    statusLabel: 'Released to Owner',
    items: [
      { key: 'notes', label: 'Notes', style: { xs: 12 } },
      { key: 'signature', label: 'Signature', style: { xs: 12 } },
      { key: 'owner', label: 'Owner', style: { xs: 12 } },
    ],
  },
  [PropertyStatus.RELEASED_TO_PERSON]: {
    statusLabel: 'Released to Person',
    items: [
      { key: 'releasedTo', label: 'Released to' },
      { key: 'phoneNumber', label: 'Phone number' },
      { key: 'expectedBackDate', label: 'Date expected back', type: 'date' },
      { key: 'signature', label: 'Signature', style: { xs: 12 } },
      { key: 'notes', label: 'Notes', style: { xs: 12 } },
    ],
  },
  [PropertyStatus.TRANSFERRED]: {
    statusLabel: 'Transferred',
    items: [
      { key: 'agency.name', label: 'Transferred to' },
      { key: 'agency.phoneNumber', label: 'Agency phone number' },
      { key: 'agency.contact.name', label: 'Agency contact person' },
      { key: 'agency.contact.phoneNumber', label: 'Contact phone number' },
      { key: 'user.name', label: 'Released to' },
      { key: 'user.badgeNumber', label: 'Badge number' },
      { key: 'expectedBackDate', label: 'Date expected back', type: 'date' },
      { key: 'signature', label: 'Signature', style: { xs: 12 } },
      {
        key: 'notes',
        label: 'Comment',
        style: { xs: 12 },
      },
    ],
  },
};
